import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Table, Button, Space, Card, Modal, Form, Input, InputNumber, Select, Tag, Popconfirm, 
  message, Row, Col, Tabs, Statistic, Tooltip, Badge, Segmented, Divider, Switch 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, MailOutlined, PhoneOutlined,
  SearchOutlined, ReloadOutlined, TeamOutlined, BookOutlined, BarChartOutlined,
  CalendarOutlined, VideoCameraOutlined, ClockCircleOutlined, DownloadOutlined,
  FilterOutlined, EyeOutlined, UserOutlined, CheckCircleOutlined, BankOutlined, CopyOutlined, DollarOutlined
} from '@ant-design/icons';
import {
  fetchInstructors, createInstructor, updateInstructor, deleteInstructor, fetchInstructorSessionCounts, copyInstructorsToAcademicYear
} from '../../../Service/Instructor_API/instructorApi';
import { fetchSubjects, fetchStandards, fetchAcademicYears } from '../../../Service/Syllabus_API/syllabusApi';

const { Option } = Select;

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === '0000-00-00') return 'N/A';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  }
  return dateStr;
};

const getCurrentMonthString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const InstructorManagementPage = () => {
  // Main Navigation Tab
  const [activeTab, setActiveTab] = useState('instructors');

  // Master Data & Tab 1 (Directory) States
  const [data, setData] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [standards, setStandards] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // Tab 1 Search & Filter States
  const [searchText, setSearchText] = useState('');
  const [mediumFilter, setMediumFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [academicYearFilter, setAcademicYearFilter] = useState('all');

  // Copy Active Instructors Modal States
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyTargetAcademicYear, setCopyTargetAcademicYear] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  const handleCopyInstructors = async () => {
    if (!copyTargetAcademicYear) {
      message.warning('Please select a target Academic Year.');
      return;
    }
    setCopyLoading(true);
    try {
      const res = await copyInstructorsToAcademicYear({
        target_academic_year_id: copyTargetAcademicYear,
        source_academic_year_id: academicYearFilter
      });
      if (res && res.message) {
        message.success(res.message);
      } else {
        message.success('Active instructors copied successfully.');
      }
      setIsCopyModalOpen(false);
      setCopyTargetAcademicYear('');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to copy instructors.');
    } finally {
      setCopyLoading(false);
    }
  };

  // Tab 2 (Instructor Session Count & Analytics) States
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionData, setSessionData] = useState({
    summary: { total_instructors: 0, total_sessions_delivered: 0, total_session_units: 0, total_videos_created: 0 },
    instructor_counts: [],
    sessions: []
  });
  const [scSearchText, setScSearchText] = useState('');
  const [scAcademicYearFilter, setScAcademicYearFilter] = useState('all');
  const [scInstructorFilter, setScInstructorFilter] = useState('all');
  const [scMonthFilter, setScMonthFilter] = useState(getCurrentMonthString());
  const [scViewMode, setScViewMode] = useState('summary'); // 'summary' | 'detailed'

  // Pay Recorded & Pay Tech Cancelled Sessions Toggle States
  const [payRecordedMap, setPayRecordedMap] = useState({});
  const [globalPayRecorded, setGlobalPayRecorded] = useState(false);

  const [payTechCancelledMap, setPayTechCancelledMap] = useState({});
  const [globalPayTechCancelled, setGlobalPayTechCancelled] = useState(false);

  const handleToggleGlobalPayRecorded = (checked) => {
    setGlobalPayRecorded(checked);
    const newMap = {};
    (sessionData.instructor_counts || []).forEach(inst => {
      newMap[inst.instructor_id] = checked;
    });
    setPayRecordedMap(newMap);
  };

  const handleToggleGlobalPayTechCancelled = (checked) => {
    setGlobalPayTechCancelled(checked);
    const newMap = {};
    (sessionData.instructor_counts || []).forEach(inst => {
      newMap[inst.instructor_id] = checked;
    });
    setPayTechCancelledMap(newMap);
  };

  const summaryTotals = useMemo(() => {
    let totalLive = 0;
    let totalRecorded = 0;
    let totalTechCancelled = 0;
    let totalPayableCount = 0;
    let totalGrossAmt = 0;
    let totalTdsAmt = 0;
    let totalNetPayable = 0;

    (sessionData.instructor_counts || []).forEach(row => {
      const isPayRecorded = !!payRecordedMap[row.instructor_id];
      const isPayTechCancelled = !!payTechCancelledMap[row.instructor_id];

      const liveCount = row.sessions_delivered_live || 0;
      const recCount = row.sessions_delivered_recorded || 0;
      const techCancelledCount = row.sessions_cancelled_technical || 0;

      const liveAmt = parseFloat(row.live_session_amount) || 0;
      const recAmt = parseFloat(row.recorded_session_amount) || 0;
      const techCancelledAmt = parseFloat(row.technical_cancelled_session_amount) || 0;

      const grossAmt = liveAmt + (isPayRecorded ? recAmt : 0) + (isPayTechCancelled ? techCancelledAmt : 0);
      const tds = parseFloat(row.tds_percent) || 0;
      const tdsAmt = grossAmt * (tds / 100);
      const net = Math.max(0, grossAmt - tdsAmt);

      totalLive += liveCount;
      totalRecorded += recCount;
      totalTechCancelled += techCancelledCount;
      totalPayableCount += liveCount + (isPayRecorded ? recCount : 0) + (isPayTechCancelled ? techCancelledCount : 0);
      totalGrossAmt += grossAmt;
      totalTdsAmt += tdsAmt;
      totalNetPayable += net;
    });

    return {
      totalLive,
      totalRecorded,
      totalTechCancelled,
      totalPayableCount,
      totalGrossAmt,
      totalTdsAmt,
      totalNetPayable
    };
  }, [sessionData.instructor_counts, payRecordedMap, payTechCancelledMap]);

  // Pagination States
  const [dirPageSize, setDirPageSize] = useState(10);
  const [dirCurrentPage, setDirCurrentPage] = useState(1);

  const [scSummaryPageSize, setScSummaryPageSize] = useState(10);
  const [scSummaryCurrentPage, setScSummaryCurrentPage] = useState(1);

  const [scDetailPageSize, setScDetailPageSize] = useState(15);
  const [scDetailCurrentPage, setScDetailCurrentPage] = useState(1);

  const getDefaultAcademicYearId = (ayList) => {
    const activeAy = (ayList || []).find(ay => ay.status === 'Active' || ay.status === 'ACTIVE');
    return activeAy ? String(activeAy.academic_year_id) : (ayList && ayList.length > 0 ? String(ayList[0].academic_year_id) : 'all');
  };

  // Load Tab 1 Directory Data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText && searchText.trim()) params.search = searchText.trim();
      if (mediumFilter && mediumFilter !== 'all') params.medium = mediumFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (subjectFilter && subjectFilter !== 'all') params.subject_id = subjectFilter;
      if (academicYearFilter && academicYearFilter !== 'all') params.academic_year_id = academicYearFilter;

      const [instRes, subjRes, stdRes, ayRes] = await Promise.all([
        fetchInstructors(params).catch(() => []),
        fetchSubjects().catch(() => []),
        fetchStandards().catch(() => []),
        fetchAcademicYears().catch(() => [])
      ]);
      setData(instRes || []);
      setSubjects(subjRes || []);
      setStandards(stdRes || []);
      setAcademicYears(ayRes || []);

      const activeAyId = getDefaultAcademicYearId(ayRes);
      if (activeAyId !== 'all') {
        setAcademicYearFilter(prev => prev === 'all' ? activeAyId : prev);
        setScAcademicYearFilter(prev => prev === 'all' ? activeAyId : prev);
      }
    } catch (e) {
      message.error('Failed to load instructors or subjects.');
    } finally {
      setLoading(false);
    }
  }, [searchText, mediumFilter, statusFilter, subjectFilter, academicYearFilter]);

  // Load Tab 2 Session Count Data
  const loadSessionCountsData = useCallback(async () => {
    setSessionLoading(true);
    try {
      const params = {};
      if (scSearchText && scSearchText.trim()) params.search = scSearchText.trim();
      if (scAcademicYearFilter && scAcademicYearFilter !== 'all') params.academic_year_id = scAcademicYearFilter;
      if (scInstructorFilter && scInstructorFilter !== 'all') params.instructor_id = scInstructorFilter;
      if (scMonthFilter && scMonthFilter !== 'all') params.month = scMonthFilter;

      const res = await fetchInstructorSessionCounts(params);
      if (res) {
        setSessionData({
          summary: res.summary || { total_instructors: 0, total_sessions_delivered: 0, total_session_units: 0, total_videos_created: 0 },
          instructor_counts: res.instructor_counts || [],
          sessions: res.sessions || []
        });
      }
    } catch (e) {
      message.error('Failed to load instructor session counts.');
    } finally {
      setSessionLoading(false);
    }
  }, [
    scSearchText, scAcademicYearFilter, scInstructorFilter, scMonthFilter
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'session-counts') {
      loadSessionCountsData();
    }
  }, [activeTab, loadSessionCountsData]);

  useEffect(() => {
    setDirCurrentPage(1);
  }, [searchText, mediumFilter, statusFilter, subjectFilter, academicYearFilter]);

  useEffect(() => {
    setScSummaryCurrentPage(1);
    setScDetailCurrentPage(1);
  }, [scSearchText, scAcademicYearFilter, scInstructorFilter, scMonthFilter]);

  // Helper to format full "Standard — Subject" string with fallback lookup
  const getSubjectFullDisplay = (sub) => {
    if (!sub) return '';
    let std = sub.standard_name || sub.subjects_standard;
    if (!std && sub.standard_id && standards && standards.length > 0) {
      const foundStd = standards.find(st => String(st.standard_id) === String(sub.standard_id));
      if (foundStd) std = foundStd.standard_name;
    }
    return std ? `${std} — ${sub.subjects_name}` : sub.subjects_name;
  };

  // Dynamic Medium list from data
  const availableMediums = useMemo(() => {
    const mediums = new Set(['KANNADA', 'ENGLISH', 'KANNADA/ENGLISH']);
    (data || []).forEach(item => {
      if (item && item.instructor_medium) mediums.add(item.instructor_medium.toUpperCase());
    });
    return Array.from(mediums);
  }, [data]);

  // Filtered instructor list based on search and selected filters (Tab 1)
  const filteredData = useMemo(() => {
    return (data || []).filter(item => {
      if (!item) return false;
      const searchLower = searchText.trim().toLowerCase();
      const matchesSearch = !searchLower ||
        (item.instructor_name && item.instructor_name.toLowerCase().includes(searchLower)) ||
        (item.instructor_email && item.instructor_email.toLowerCase().includes(searchLower)) ||
        (item.instructor_contact && item.instructor_contact.toLowerCase().includes(searchLower)) ||
        (item.instructor_qualification && item.instructor_qualification.toLowerCase().includes(searchLower));

      const matchesMedium = mediumFilter === 'all' ||
        (item.instructor_medium && item.instructor_medium.toUpperCase() === mediumFilter.toUpperCase());

      const matchesStatus = statusFilter === 'all' ||
        (item.status && item.status.toLowerCase() === statusFilter.toLowerCase());

      const matchesSubject = subjectFilter === 'all' || (() => {
        if (!item.subjects_id) return false;
        const assignedIds = String(item.subjects_id).split(',').map(s => s.trim());
        return assignedIds.includes(String(subjectFilter));
      })();

      return matchesSearch && matchesMedium && matchesStatus && matchesSubject;
    });
  }, [data, searchText, mediumFilter, statusFilter, subjectFilter]);

  const handleAcademicYearFilterChange = (val) => {
    setAcademicYearFilter(val);
    setSubjectFilter('all');
  };

  const handleClearFilters = () => {
    setSearchText('');
    setMediumFilter('all');
    setStatusFilter('all');
    setSubjectFilter('all');
    setAcademicYearFilter(getDefaultAcademicYearId(academicYears));
  };

  const handleClearSessionFilters = () => {
    setScSearchText('');
    setScAcademicYearFilter(getDefaultAcademicYearId(academicYears));
    setScInstructorFilter('all');
    setScMonthFilter(getCurrentMonthString());
  };

  const handleSave = async (values) => {
    try {
      const selectedYearIds = Array.isArray(values.subjects_id) ? values.subjects_id.map(id => parseInt(id)) : [];
      
      let finalSubjectsIds = [...selectedYearIds];
      if (editingItem && editingItem.subjects_id && academicYearFilter !== 'all') {
        const allExistingIds = String(editingItem.subjects_id).split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
        
        // Find existing assigned subjects that belong to OTHER academic years
        const otherYearsIds = allExistingIds.filter(id => {
          const subObj = subjects.find(s => String(s.subjects_id) === String(id));
          return !subObj || String(subObj.academic_year_id) !== String(academicYearFilter);
        });
        
        finalSubjectsIds = [...otherYearsIds, ...selectedYearIds];
      }

      const payload = {
        ...values,
        per_session_payment: values.per_session_payment ? parseFloat(values.per_session_payment) : 0.00,
        tds_percent: values.tds_percent ? parseFloat(values.tds_percent) : 0.00,
        academic_year_id: values.academic_year_id || (academicYearFilter !== 'all' ? academicYearFilter : getDefaultAcademicYearId(academicYears)),
        subjects_id: finalSubjectsIds.length > 0 ? finalSubjectsIds.join(',') : null,
        subject_rates: values.subject_rates || {}
      };

      if (editingItem) {
        await updateInstructor(editingItem.instructor_id, payload);
        message.success('Instructor updated successfully.');
      } else {
        await createInstructor(payload);
        message.success('Instructor created successfully.');
      }
      setIsModalOpen(false);
      setEditingItem(null);
      form.resetFields();
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to save instructor.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteInstructor(id);
      message.success('Instructor deleted successfully.');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to delete instructor.');
    }
  };

  // CSV Export for Instructor Session Summary or Session Logs
  const exportSessionReport = () => {
    if (scViewMode === 'summary') {
      if (!sessionData.instructor_counts || sessionData.instructor_counts.length === 0) {
        message.warning('No data to export.');
        return;
      }
      const headers = [
        'Instructor ID', 'Instructor Name', 'Qualification', 
        'Sessions Delivered (Live)', 'Sessions Delivered (Recorded)', 'Total Session Payable', 
        'Total Session Amount (INR)', 'TDS Percent (%)', 'TDS Amount (INR)', 'Payable After TDS (INR)', 
        'Recorded Sessions Paid', 'Latest Session Date'
      ];
      const csvRows = [headers.join(',')];
      
      sessionData.instructor_counts.forEach(row => {
        const isPayRecorded = !!payRecordedMap[row.instructor_id];
        const liveCount = row.sessions_delivered_live || 0;
        const recCount = row.sessions_delivered_recorded || 0;
        const liveAmt = parseFloat(row.live_session_amount) || 0;
        const recAmt = parseFloat(row.recorded_session_amount) || 0;

        const totalPayableCount = liveCount + (isPayRecorded ? recCount : 0);
        const grossAmt = liveAmt + (isPayRecorded ? recAmt : 0);
        const tdsPercent = parseFloat(row.tds_percent) || 0;
        const tdsAmt = grossAmt * (tdsPercent / 100);
        const netPayable = Math.max(0, grossAmt - tdsAmt);

        const line = [
          row.instructor_id,
          `"${(row.instructor_name || '').replace(/"/g, '""')}"`,
          `"${(row.instructor_qualification || '').replace(/"/g, '""')}"`,
          liveCount,
          recCount,
          totalPayableCount,
          grossAmt.toFixed(2),
          tdsPercent.toFixed(2),
          tdsAmt.toFixed(2),
          netPayable.toFixed(2),
          isPayRecorded ? 'Yes' : 'No',
          `"${row.last_session_date}"`
        ];
        csvRows.push(line.join(','));
      });

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Instructor_Session_Counts_Summary_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Session count summary exported successfully!');
    } else {
      if (!sessionData.sessions || sessionData.sessions.length === 0) {
        message.warning('No session logs to export.');
        return;
      }
      const headers = ['Session ID', 'Date', 'Start Time', 'End Time', 'Instructor', 'Standard', 'Subject', 'Topic / Part', 'Session Type', 'Session Count Units', 'Session Rate (INR)', 'Session Amount (INR)', 'Cluster', 'Academic Year'];
      const csvRows = [headers.join(',')];

      sessionData.sessions.forEach(s => {
        const line = [
          s.daily_session_id,
          `"${s.daily_session_date || ''}"`,
          `"${s.start_time || ''}"`,
          `"${s.end_time || ''}"`,
          `"${(s.instructor_name || '').replace(/"/g, '""')}"`,
          `"${(s.standard_name || '').replace(/"/g, '""')}"`,
          `"${(s.subjects_name || '').replace(/"/g, '""')}"`,
          `"${(s.daily_session_part || s.lesson_name || '').replace(/"/g, '""')}"`,
          `"${s.session_type || ''}"`,
          s.session_count,
          s.session_rate || 0,
          s.session_amount || 0,
          `"${(s.cluster_name || '').replace(/"/g, '""')}"`,
          `"${(s.academic_year || '').replace(/"/g, '""')}"`
        ];
        csvRows.push(line.join(','));
      });

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Instructor_Detailed_Session_Logs_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Detailed session logs exported successfully!');
    }
  };

  // Directory Columns (Tab 1)
  const columns = [
    {
      title: 'Instructor Name',
      key: 'instructor_name',
      sorter: (a, b) => (a.instructor_name || '').localeCompare(b.instructor_name || ''),
      render: (_, record) => {
        const medium = record.instructor_medium;
        const mediumColor = medium === 'ENGLISH' ? 'blue' : medium === 'KANNADA' ? 'orange' : 'purple';
        const ayObj = academicYears.find(ay => String(ay.academic_year_id) === String(record.academic_year_id));

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-color)', fontSize: 14 }}>
              {record.instructor_name || 'N/A'}
            </span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {medium && (
                <Tag color={mediumColor} style={{ fontWeight: 600, fontSize: 11, margin: 0 }}>
                  {medium}
                </Tag>
              )}
              {ayObj && (
                <Tag color="purple" style={{ fontWeight: 600, fontSize: 11, margin: 0 }}>
                  {ayObj.academic_year}
                </Tag>
              )}
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {record.instructor_qualification || 'Qualifications N/A'}
            </span>
          </div>
        );
      }
    },
    {
      title: 'Contact Details',
      key: 'contact_details',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 13 }}>
          {record.instructor_email && (
            <span><MailOutlined style={{ marginRight: 6, color: '#1890ff' }} />{record.instructor_email}</span>
          )}
          {record.instructor_contact && (
            <span><PhoneOutlined style={{ marginRight: 6, color: '#10b981' }} />{record.instructor_contact}</span>
          )}
          {!record.instructor_email && !record.instructor_contact && (
            <span style={{ color: 'var(--text-secondary)' }}>No Contact Info</span>
          )}
        </div>
      )
    },
    {
      title: 'Assigned Standard - Subject(s)',
      key: 'assigned_subjects',
      render: (_, record) => {
        if (!record.subjects_id) return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>None</span>;
        const ids = String(record.subjects_id).split(',').map(s => s.trim());
        const matched = subjects.filter(sub => 
          ids.includes(String(sub.subjects_id)) &&
          (academicYearFilter === 'all' || String(sub.academic_year_id) === String(academicYearFilter))
        );
        if (matched.length === 0) return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>None</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
            {matched.map(sub => (
              <Tag key={sub.subjects_id} color="cyan" style={{ fontWeight: 600, borderRadius: 4, margin: 0 }}>
                <BookOutlined style={{ marginRight: 4 }} />
                {getSubjectFullDisplay(sub)}
              </Tag>
            ))}
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 'Active' ? 'green' : 'red'} style={{ fontWeight: 700 }}>{(status || 'Active').toUpperCase()}</Tag>
    },
    {
      title: 'Bank Account & TDS',
      key: 'bank_details',
      render: (_, record) => {
        const tds = parseFloat(record.tds_percent) || 0;
        if (!record.account_number && !record.bank_account_name && !record.ifsc_code && !record.pan_number && tds === 0) {
          return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Not Provided</span>;
        }
        return (
          <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-color)' }}>
              {record.bank_account_name || record.instructor_name}
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>
              {record.bank_name || 'Bank N/A'} {record.branch_name ? `(${record.branch_name})` : ''}
            </div>
            <div style={{ fontFamily: 'monospace', color: '#1890ff' }}>
              A/C: {record.account_number || 'N/A'}
            </div>
            {record.ifsc_code && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                IFSC: <span style={{ fontWeight: 600 }}>{record.ifsc_code}</span>
              </div>
            )}
            {record.pan_number && (
              <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 600 }}>
                PAN: {record.pan_number}
              </div>
            )}
            {tds > 0 ? (
              <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                TDS: {tds}%
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#888' }}>TDS: 0%</div>
            )}
          </div>
        );
      }
    },
    { 
      title: 'Join & Exit Dates', 
      key: 'join_exit_dates', 
      render: (_, record) => (
        <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>Joined: </span>
            <span style={{ fontWeight: 500 }}>{formatDate(record.join_date)}</span>
          </div>
          {record.exit_date && record.exit_date !== '0000-00-00' && (
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>Exited: </span>
              <span style={{ color: '#ef4444', fontWeight: 500 }}>{formatDate(record.exit_date)}</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { 
            let subjectIds = [];
            if (record.subjects_id) {
              const allIds = String(record.subjects_id).split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
              subjectIds = allIds.filter(id => {
                const subObj = subjects.find(s => String(s.subjects_id) === String(id));
                return !subObj || academicYearFilter === 'all' || String(subObj.academic_year_id) === String(academicYearFilter);
              });
            }
            let parsedRates = {};
            if (record.subject_rates) {
              try {
                parsedRates = typeof record.subject_rates === 'string' ? JSON.parse(record.subject_rates) : record.subject_rates;
              } catch(e) {
                parsedRates = {};
              }
            }
            const formattedRecord = {
              ...record,
              per_session_payment: record.per_session_payment ? parseFloat(record.per_session_payment) : 0,
              tds_percent: record.tds_percent ? parseFloat(record.tds_percent) : 0,
              academic_year_id: record.academic_year_id ? String(record.academic_year_id) : (academicYearFilter !== 'all' ? academicYearFilter : getDefaultAcademicYearId(academicYears)),
              subjects_id: subjectIds,
              subject_rates: parsedRates,
              join_date: record.join_date || '',
              exit_date: record.exit_date || '',
            };
            setEditingItem(record); 
            form.setFieldsValue(formattedRecord); 
            setIsModalOpen(true); 
          }} />
          <Popconfirm title="Delete this instructor?" onConfirm={() => handleDelete(record.instructor_id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Tab 2 Session Count Summary Columns
  const sessionSummaryColumns = [
    {
      title: 'Instructor Name',
      key: 'instructor_name',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-color)', fontSize: 14 }}>
            {record.instructor_name || 'N/A'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {record.instructor_qualification || 'Qualifications N/A'}
          </span>
        </div>
      )
    },
    {
      title: 'Sessions Breakdown',
      key: 'sessions_breakdown',
      align: 'center',
      sorter: (a, b) => 
        ((a.sessions_delivered_live || 0) + (a.sessions_delivered_recorded || 0) + (a.sessions_cancelled_technical || 0)) -
        ((b.sessions_delivered_live || 0) + (b.sessions_delivered_recorded || 0) + (b.sessions_cancelled_technical || 0)),
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
          <Tag color="green" style={{ fontWeight: 700, fontSize: 12, margin: 0, minWidth: 110, textAlign: 'center' }}>
            {record.sessions_delivered_live || 0} Live
          </Tag>
          <Tag color="purple" style={{ fontWeight: 700, fontSize: 12, margin: 0, minWidth: 110, textAlign: 'center' }}>
            {record.sessions_delivered_recorded || 0} Recorded
          </Tag>
          <Tag color="orange" style={{ fontWeight: 700, fontSize: 12, margin: 0, minWidth: 110, textAlign: 'center' }}>
            {record.sessions_cancelled_technical || 0} Tech Issue
          </Tag>
        </div>
      )
    },
    {
      title: 'Total Session Payable',
      key: 'total_session_payable',
      align: 'center',
      render: (_, record) => {
        const isPayRecorded = !!payRecordedMap[record.instructor_id];
        const isPayTechCancelled = !!payTechCancelledMap[record.instructor_id];
        const liveCount = record.sessions_delivered_live || 0;
        const recCount = record.sessions_delivered_recorded || 0;
        const techCancelledCount = record.sessions_cancelled_technical || 0;
        const totalPayableCount = liveCount + (isPayRecorded ? recCount : 0) + (isPayTechCancelled ? techCancelledCount : 0);
        return (
          <Tag color="blue" style={{ fontWeight: 700, fontSize: 13, padding: '3px 10px' }}>
            {totalPayableCount} Sessions
          </Tag>
        );
      }
    },
    {
      title: 'Total Session Amount',
      key: 'total_session_amount',
      align: 'right',
      render: (_, record) => {
        const isPayRecorded = !!payRecordedMap[record.instructor_id];
        const isPayTechCancelled = !!payTechCancelledMap[record.instructor_id];
        const liveAmt = parseFloat(record.live_session_amount) || 0;
        const recAmt = parseFloat(record.recorded_session_amount) || 0;
        const techCancelledAmt = parseFloat(record.technical_cancelled_session_amount) || 0;
        const totalAmt = liveAmt + (isPayRecorded ? recAmt : 0) + (isPayTechCancelled ? techCancelledAmt : 0);
        return (
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-color)' }}>
            ₹{totalAmt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        );
      }
    },
    {
      title: 'TDS Amount',
      key: 'tds_amount',
      align: 'right',
      render: (_, record) => {
        const isPayRecorded = !!payRecordedMap[record.instructor_id];
        const isPayTechCancelled = !!payTechCancelledMap[record.instructor_id];
        const liveAmt = parseFloat(record.live_session_amount) || 0;
        const recAmt = parseFloat(record.recorded_session_amount) || 0;
        const techCancelledAmt = parseFloat(record.technical_cancelled_session_amount) || 0;
        const totalAmt = liveAmt + (isPayRecorded ? recAmt : 0) + (isPayTechCancelled ? techCancelledAmt : 0);
        const tdsPercent = parseFloat(record.tds_percent) || 0;
        const tdsAmt = totalAmt * (tdsPercent / 100);
        return (
          <div style={{ fontSize: 12 }}>
            <div style={{ color: '#ef4444', fontWeight: 600 }}>
              -₹{tdsAmt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 11, color: '#888' }}>({tdsPercent}% TDS)</div>
          </div>
        );
      }
    },
    {
      title: 'Payable After TDS Deduction',
      key: 'payable_after_tds',
      align: 'right',
      render: (_, record) => {
        const isPayRecorded = !!payRecordedMap[record.instructor_id];
        const isPayTechCancelled = !!payTechCancelledMap[record.instructor_id];
        const liveAmt = parseFloat(record.live_session_amount) || 0;
        const recAmt = parseFloat(record.recorded_session_amount) || 0;
        const techCancelledAmt = parseFloat(record.technical_cancelled_session_amount) || 0;
        const totalAmt = liveAmt + (isPayRecorded ? recAmt : 0) + (isPayTechCancelled ? techCancelledAmt : 0);
        const tdsPercent = parseFloat(record.tds_percent) || 0;
        const tdsAmt = totalAmt * (tdsPercent / 100);
        const netPayable = Math.max(0, totalAmt - tdsAmt);
        return (
          <div style={{ fontWeight: 700, fontSize: 14, color: '#10b981' }}>
            ₹{netPayable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => {
        const isPayRecorded = !!payRecordedMap[record.instructor_id];
        const isPayTechCancelled = !!payTechCancelledMap[record.instructor_id];
        return (
          <Space direction="vertical" size="small" align="center">
            <Tooltip title="Enable to include Recorded Sessions in payable calculation">
              <Switch
                checkedChildren="Pay Recorded"
                unCheckedChildren="Skip Recorded"
                checked={isPayRecorded}
                onChange={(checked) => {
                  setPayRecordedMap(prev => ({
                    ...prev,
                    [record.instructor_id]: checked
                  }));
                }}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Enable to include Technical Cancelled Sessions in payable calculation">
              <Switch
                checkedChildren="Pay Tech Issue"
                unCheckedChildren="Skip Tech Issue"
                checked={isPayTechCancelled}
                onChange={(checked) => {
                  setPayTechCancelledMap(prev => ({
                    ...prev,
                    [record.instructor_id]: checked
                  }));
                }}
                size="small"
              />
            </Tooltip>
            <Button 
              size="small" 
              type="primary" 
              ghost 
              icon={<EyeOutlined />} 
              onClick={() => {
                setScInstructorFilter(String(record.instructor_id));
                setScViewMode('detailed');
              }}
            >
              View Logs
            </Button>
          </Space>
        );
      }
    }
  ];

  // Tab 2 Detailed Session Logs Columns
  const detailedSessionColumns = [
    {
      title: 'Date & Time',
      key: 'date_time',
      sorter: (a, b) => new Date(a.daily_session_date) - new Date(b.daily_session_date),
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-color)' }}>
            <CalendarOutlined style={{ marginRight: 4, color: '#1890ff' }} />
            {formatDate(record.daily_session_date)}
          </div>
          {(record.start_time || record.end_time) && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              <ClockCircleOutlined style={{ marginRight: 3 }} />
              {record.start_time || '—'} to {record.end_time || '—'}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Instructor Name',
      dataIndex: 'instructor_name',
      key: 'instructor_name',
      render: (name) => <span style={{ fontWeight: 600 }}>{name || 'N/A'}</span>
    },
    {
      title: 'Standard & Subject',
      key: 'std_sub',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1890ff' }}>{record.standard_name || 'Standard N/A'}</div>
          <Tag color="cyan" style={{ fontSize: 11, marginTop: 2 }}>{record.subjects_name || 'Subject N/A'}</Tag>
        </div>
      )
    },
    {
      title: 'Lesson / Topic',
      key: 'topic',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.lesson_name || 'Lesson N/A'}</div>
          {record.daily_session_part && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Part/Topic: {record.daily_session_part}</div>
          )}
        </div>
      )
    },
    {
      title: 'Session Type / Status',
      key: 'session_type_status',
      render: (_, record) => {
        if (record.is_cancelled) {
          return <Tag color="red" style={{ fontWeight: 700 }}>CANCELLED</Tag>;
        }
        return <Tag color={record.session_type === 'RECORDED' ? 'purple' : 'blue'} style={{ fontWeight: 600 }}>{record.session_type || 'LIVE'}</Tag>;
      }
    },
    {
      title: 'Session Count (Units)',
      dataIndex: 'session_count',
      key: 'session_count',
      align: 'center',
      render: (cnt, record) => record.is_cancelled ? <Tag color="red" style={{ fontWeight: 600 }}>0 Units (Cancelled)</Tag> : <Tag color="purple" style={{ fontWeight: 700 }}>{cnt || 1.0} Unit</Tag>
    },
    {
      title: 'Session Amount (₹)',
      key: 'session_amount',
      align: 'right',
      render: (_, record) => {
        if (record.is_cancelled) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <Tag color="red" style={{ fontWeight: 700, fontSize: 12, margin: 0 }}>
                ₹0.00 (Cancelled)
              </Tag>
            </div>
          );
        }
        const rate = parseFloat(record.session_rate || 0);
        const amount = parseFloat(record.session_amount || 0);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <Tag color="green" style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>
              ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </Tag>
            {rate > 0 && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Rate: ₹{rate}/unit
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: 'Cluster / Location',
      dataIndex: 'cluster_name',
      key: 'cluster_name',
      render: (cName) => cName ? <Tag color="magenta">{cName}</Tag> : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>N/A</span>
    }
  ];

  const hasActiveDirectoryFilters = searchText || mediumFilter !== 'all' || statusFilter !== 'all' || subjectFilter !== 'all' || academicYearFilter !== getDefaultAcademicYearId(academicYears);
  const hasActiveSessionFilters = scSearchText || scAcademicYearFilter !== getDefaultAcademicYearId(academicYears) || scInstructorFilter !== 'all' || scMonthFilter !== getCurrentMonthString();

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', borderRadius: 12, border: '1px solid var(--border-color)' }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space size="middle">
          <TeamOutlined style={{ fontSize: 26, color: '#1890ff' }} />
          <div>
            <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Instructor Management</h2>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Manage instructors, assignments, and track session counts & analytics</span>
          </div>
        </Space>
        <Space>
          {activeTab === 'instructors' && (
            <>
              <Button 
                type="primary" 
                style={{ background: '#7c3aed', borderColor: '#7c3aed' }} 
                icon={<CopyOutlined />} 
                onClick={() => setIsCopyModalOpen(true)}
              >
                Copy Active Instructors
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setIsModalOpen(true); }}>
                Add Instructor
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* Main Page Tabs */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab} 
        type="card"
        style={{ marginBottom: 16 }}
      >
        {/* Tab 1: Instructor Directory */}
        <Tabs.TabPane 
          tab={
            <span>
              <UserOutlined style={{ marginRight: 6 }} />
              Instructors Directory
            </span>
          } 
          key="instructors"
        >
          <Card style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
            {/* Search & Filter Toolbar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <Input
                placeholder="Search by Name, Email, Contact, or Qualification..."
                prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ width: 320, borderRadius: 8 }}
              />

              <Select showSearch optionFilterProp="children"
                value={academicYearFilter}
                onChange={handleAcademicYearFilterChange}
                style={{ width: 180 }}
                placeholder="Academic Year"
              >
                <Option value="all">All Academic Years</Option>
                {academicYears.map(ay => (
                  <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                    {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                  </Option>
                ))}
              </Select>

              <Select showSearch optionFilterProp="children"
                value={subjectFilter}
                onChange={setSubjectFilter}
                style={{ width: 220 }}
                placeholder="Filter by Subject"
              >
                <Option value="all">All Subjects</Option>
                {subjects
                  .filter(s => academicYearFilter === 'all' || String(s.academic_year_id) === String(academicYearFilter))
                  .map(s => (
                    <Option key={s.subjects_id} value={String(s.subjects_id)}>
                      {getSubjectFullDisplay(s)}
                    </Option>
                  ))}
              </Select>

              <Select showSearch optionFilterProp="children"
                value={mediumFilter}
                onChange={setMediumFilter}
                style={{ width: 140 }}
                placeholder="Medium"
              >
                <Option value="all">All Mediums</Option>
                {availableMediums.map(m => (
                  <Option key={m} value={m}>{m}</Option>
                ))}
              </Select>

              <Select showSearch optionFilterProp="children"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 130 }}
                placeholder="Status"
              >
                <Option value="all">All Statuses</Option>
                <Option value="Active">Active</Option>
                <Option value="Inactive">Inactive</Option>
              </Select>

              {hasActiveDirectoryFilters && (
                <Button onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>

            <Table 
              dataSource={filteredData} 
              columns={columns} 
              rowKey="instructor_id" 
              loading={loading} 
              scroll={{ x: 'max-content' }}
              pagination={{ 
                current: dirCurrentPage,
                pageSize: dirPageSize,
                pageSizeOptions: ['10', '25', '50', '100'],
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} instructors`,
                onChange: (page, size) => {
                  setDirCurrentPage(page);
                  setDirPageSize(size);
                }
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Tab 2: Instructor Session Counts & Analytics */}
        <Tabs.TabPane 
          tab={
            <span>
              <BarChartOutlined style={{ marginRight: 6 }} />
              Instructor Session Count & Analytics
            </span>
          } 
          key="session-counts"
        >

          <Card style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
            {/* Filter Toolbar for Session Counts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <Input
                  placeholder="Search Instructor, Subject, Topic..."
                  prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                  value={scSearchText}
                  onChange={(e) => setScSearchText(e.target.value)}
                  allowClear
                  style={{ width: 280, borderRadius: 8 }}
                />

                <Select 
                  showSearch 
                  optionFilterProp="children"
                  value={scAcademicYearFilter}
                  onChange={setScAcademicYearFilter}
                  style={{ width: 170 }}
                  placeholder="Academic Year"
                >
                  <Option value="all">All Academic Years</Option>
                  {academicYears.map(ay => (
                    <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                      {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                    </Option>
                  ))}
                </Select>

                <Select 
                  showSearch 
                  optionFilterProp="children"
                  value={scInstructorFilter}
                  onChange={setScInstructorFilter}
                  style={{ width: 200 }}
                  placeholder="Select Instructor"
                >
                  <Option value="all">All Instructors</Option>
                  {data
                    .filter(inst => (inst.status || '').toLowerCase() === 'active')
                    .map(inst => (
                      <Option key={inst.instructor_id} value={String(inst.instructor_id)}>
                        {inst.instructor_name}
                      </Option>
                    ))}
                </Select>

                <Input 
                  type="month" 
                  value={scMonthFilter === 'all' ? '' : scMonthFilter} 
                  onChange={(e) => setScMonthFilter(e.target.value || 'all')} 
                  style={{ width: 160, borderRadius: 8 }} 
                  title="Filter by Month"
                />

                {hasActiveSessionFilters && (
                  <Button onClick={handleClearSessionFilters} icon={<FilterOutlined />}>
                    Clear Filters
                  </Button>
                )}
              </div>

              {/* View Controls & Pay Global Toggles */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                {scViewMode === 'summary' ? (
                  <Space style={{ flexWrap: 'wrap' }}>
                    <Space style={{ padding: '6px 14px', background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>Pay Recorded (All):</span>
                      <Switch
                        checkedChildren="All Paid"
                        unCheckedChildren="Skip Recorded"
                        checked={globalPayRecorded}
                        onChange={handleToggleGlobalPayRecorded}
                        size="small"
                      />
                    </Space>
                    <Space style={{ padding: '6px 14px', background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>Pay Tech Issue (All):</span>
                      <Switch
                        checkedChildren="All Paid"
                        unCheckedChildren="Skip Tech Issue"
                        checked={globalPayTechCancelled}
                        onChange={handleToggleGlobalPayTechCancelled}
                        size="small"
                      />
                    </Space>
                  </Space>
                ) : <div />}

                <Segmented
                  value={scViewMode}
                  onChange={setScViewMode}
                  options={[
                    { label: 'Instructor Summary', value: 'summary', icon: <TeamOutlined /> },
                    { label: `Detailed Session Logs (${sessionData.sessions.length})`, value: 'detailed', icon: <BookOutlined /> }
                  ]}
                />
              </div>
            </div>

            {scViewMode === 'summary' && (
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={4} style={{ flex: 1, minWidth: 160 }}>
                  <Card size="small" style={{ borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--panel-color)' }}>
                    <Statistic 
                      title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Live Sessions</span>}
                      value={summaryTotals.totalLive} 
                      valueStyle={{ color: '#10b981', fontWeight: 700, fontSize: 18 }}
                      prefix={<VideoCameraOutlined style={{ marginRight: 6 }} />} 
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={4} style={{ flex: 1, minWidth: 160 }}>
                  <Card size="small" style={{ borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--panel-color)' }}>
                    <Statistic 
                      title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Recorded Sessions</span>}
                      value={summaryTotals.totalRecorded} 
                      valueStyle={{ color: '#8b5cf6', fontWeight: 700, fontSize: 18 }}
                      prefix={<VideoCameraOutlined style={{ marginRight: 6 }} />} 
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={4} style={{ flex: 1, minWidth: 160 }}>
                  <Card size="small" style={{ borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--panel-color)' }}>
                    <Statistic 
                      title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Tech Issue Cancelled</span>}
                      value={summaryTotals.totalTechCancelled} 
                      valueStyle={{ color: '#f97316', fontWeight: 700, fontSize: 18 }}
                      prefix={<VideoCameraOutlined style={{ marginRight: 6 }} />} 
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ flex: 1, minWidth: 180 }}>
                  <Card size="small" style={{ borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--panel-color)' }}>
                    <Statistic 
                      title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Gross Amount</span>}
                      value={summaryTotals.totalGrossAmt} 
                      precision={2}
                      prefix="₹" 
                      valueStyle={{ color: '#1890ff', fontWeight: 700, fontSize: 18 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ flex: 1, minWidth: 180 }}>
                  <Card size="small" style={{ borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--panel-color)' }}>
                    <Statistic 
                      title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Payable After TDS</span>}
                      value={summaryTotals.totalNetPayable} 
                      precision={2}
                      prefix="₹" 
                      valueStyle={{ color: '#059669', fontWeight: 700, fontSize: 18 }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {scViewMode === 'summary' ? (
              <Table 
                dataSource={sessionData.instructor_counts} 
                columns={sessionSummaryColumns} 
                rowKey="instructor_id" 
                loading={sessionLoading} 
                scroll={{ x: 'max-content' }}
                pagination={{ 
                  current: scSummaryCurrentPage,
                  pageSize: scSummaryPageSize,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} instructors`,
                  onChange: (page, size) => {
                    setScSummaryCurrentPage(page);
                    setScSummaryPageSize(size);
                  }
                }}
              />
            ) : (
              <div>
                {scInstructorFilter !== 'all' && (
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(24, 144, 255, 0.1)', border: '1px solid #91d5ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      Showing detailed sessions for instructor: <strong>{data.find(i => String(i.instructor_id) === String(scInstructorFilter))?.instructor_name || `ID #${scInstructorFilter}`}</strong>
                    </span>
                    <Button size="small" type="link" onClick={() => setScInstructorFilter('all')}>Show All Instructors</Button>
                  </div>
                )}
                <Table 
                  dataSource={sessionData.sessions} 
                  columns={detailedSessionColumns} 
                  rowKey="daily_session_id" 
                  loading={sessionLoading} 
                  scroll={{ x: 'max-content' }}
                  pagination={{ 
                    current: scDetailCurrentPage,
                    pageSize: scDetailPageSize,
                    pageSizeOptions: ['15', '30', '50', '100'],
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} sessions`,
                    onChange: (page, size) => {
                      setScDetailCurrentPage(page);
                      setScDetailPageSize(size);
                    }
                  }}
                />
              </div>
            )}
          </Card>
        </Tabs.TabPane>
      </Tabs>

      {/* Add / Edit Instructor Modal */}
      <Modal 
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <span>{editingItem ? 'Edit Instructor' : 'Add Instructor'}</span>
          </Space>
        } 
        open={isModalOpen} 
        onCancel={() => { setIsModalOpen(false); form.resetFields(); setEditingItem(null); }} 
        footer={null} 
        destroyOnClose
        width={720}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSave} 
          initialValues={{ status: 'Active', per_session_payment: 0, tds_percent: 0, academic_year_id: academicYearFilter !== 'all' ? academicYearFilter : getDefaultAcademicYearId(academicYears) }}
        >
          <Tabs defaultActiveKey="1" items={[
            {
              key: '1',
              label: (
                <span>
                  <UserOutlined /> Basic Information
                </span>
              ),
              children: (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="instructor_name" label="Instructor Name" rules={[{ required: true, message: 'Please enter instructor name!' }]}>
                      <Input placeholder="e.g. Dr. Anand Sharma" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="academic_year_id" label="Academic Year">
                      <Select showSearch optionFilterProp="children" placeholder="Select Academic Year">
                        {academicYears.map(ay => (
                          <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                            {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="instructor_email" label="Email Address" rules={[{ type: 'email', message: 'Please enter a valid email!' }]}>
                      <Input placeholder="e.g. instructor@example.com" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="instructor_contact" label="Phone Number">
                      <Input placeholder="e.g. 9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="instructor_qualification" label="Qualification">
                      <Input placeholder="e.g. Ph.D, M.Tech, MA" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="instructor_medium" label="Instruction Medium">
                      <Select showSearch optionFilterProp="children" placeholder="Select instructional language medium">
                        <Option value="KANNADA">KANNADA</Option>
                        <Option value="ENGLISH">ENGLISH</Option>
                        <Option value="KANNADA/ENGLISH">KANNADA/ENGLISH</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="children" placeholder="Select status">
                        <Option value="Active">Active</Option>
                        <Option value="Inactive">Inactive</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="join_date" label="Join Date">
                      <Input type="date" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="exit_date" label="Exit Date">
                      <Input type="date" />
                    </Form.Item>
                  </Col>
                </Row>
              )
            },
            {
              key: '2',
              label: (
                <span>
                  <BookOutlined /> Subject & Payment Rates
                </span>
              ),
              children: (
                <div>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="tds_percent" label="TDS Deduction (%)">
                        <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} placeholder="e.g. 10" addonAfter="%" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item 
                    name="subjects_id" 
                    label="Assign Standard - Subject(s) (One subject can have multiple instructors)"
                    help={academicYearFilter !== 'all' ? `Displaying subjects for academic year: ${academicYears.find(ay => String(ay.academic_year_id) === String(academicYearFilter))?.academic_year || ''}` : null}
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      optionFilterProp="children"
                      placeholder="Select Standard - Subject(s) assigned to this instructor"
                      allowClear
                    >
                      {subjects
                        .filter(sub => academicYearFilter === 'all' || String(sub.academic_year_id) === String(academicYearFilter))
                        .map(sub => (
                          <Option key={sub.subjects_id} value={sub.subjects_id}>
                            {getSubjectFullDisplay(sub)} {sub.subjects_medium ? `[${sub.subjects_medium}]` : ''}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>

                  {/* Dynamic Subject-Wise Payment Rates */}
                  <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.subjects_id !== currentValues.subjects_id}>
                    {({ getFieldValue }) => {
                      const selectedSubjectIds = getFieldValue('subjects_id') || [];
                      if (!selectedSubjectIds || selectedSubjectIds.length === 0) return null;
                      
                      const selectedSubs = subjects.filter(s => selectedSubjectIds.includes(s.subjects_id) || selectedSubjectIds.includes(String(s.subjects_id)));
                      if (selectedSubs.length === 0) return null;

                      return (
                        <div style={{ marginTop: 10, marginBottom: 16, padding: '14px 16px', background: 'var(--panel-color)', border: '1px dashed var(--border-color)', borderRadius: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#3b82f6', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <BookOutlined /> Subject-Wise Override Rates (₹):
                          </div>
                          <Row gutter={[12, 8]}>
                            {selectedSubs.map(sub => (
                              <Col span={12} key={sub.subjects_id}>
                                <Form.Item 
                                  name={['subject_rates', String(sub.subjects_id)]} 
                                  label={<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-color)' }}>{getSubjectFullDisplay(sub)}</span>}
                                  style={{ marginBottom: 4 }}
                                >
                                  <Input type="number" step="10" placeholder="e.g. 500" prefix="₹" size="small" />
                                </Form.Item>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      );
                    }}
                  </Form.Item>
                </div>
              )
            },
            {
              key: '3',
              label: (
                <span>
                  <BankOutlined /> Bank Account Details
                </span>
              ),
              children: (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="bank_name" label="Bank Name">
                      <Input placeholder="e.g. State Bank of India" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="bank_account_name" label="Account Holder Name">
                      <Input placeholder="Full Name as per Bank A/C" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="account_number" label="Account Number">
                      <Input placeholder="e.g. 30123456789" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="ifsc_code" label="IFSC Code">
                      <Input placeholder="e.g. SBIN0001234" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="branch_name" label="Branch Name">
                      <Input placeholder="e.g. Main Branch, City" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="pan_number" label="PAN Number">
                      <Input placeholder="e.g. ABCDE1234F" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>
                </Row>
              )
            }
          ]} />

          <Divider style={{ margin: '16px 0' }} />

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setIsModalOpen(false); form.resetFields(); setEditingItem(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save Instructor</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Copy Active Instructors Modal */}
      <Modal
        title={
          <span style={{ color: '#1890ff', fontSize: 16 }}>
            <CopyOutlined style={{ marginRight: 6 }} /> Copy Active Instructors to Academic Year
          </span>
        }
        open={isCopyModalOpen}
        onCancel={() => { setIsCopyModalOpen(false); setCopyTargetAcademicYear(''); }}
        onOk={handleCopyInstructors}
        confirmLoading={copyLoading}
        okText="Copy Active Instructors"
        destroyOnClose
      >
        <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          This will copy all <strong>Active instructors</strong> (including full <strong>Bank Details</strong>, contact info, and subject payment rates) into the target Academic Year without overwriting existing entries.
        </div>

        <Form layout="vertical">
          <Form.Item label="Target Academic Year" required>
            <Select
              showSearch
              optionFilterProp="children"
              value={copyTargetAcademicYear}
              onChange={setCopyTargetAcademicYear}
              placeholder="Select Target Academic Year to Copy To"
              style={{ width: '100%' }}
            >
              {academicYears.map(ay => (
                <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                  {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstructorManagementPage;
